import React from "react";
import { useTranslation } from "react-i18next";
import { MapPin, User, Briefcase, Bot } from "lucide-react";
import type { Job } from "../../../hooks/candidate/useCandidateData";

interface AssessmentTabProps {
  jobsLoading: boolean;
  jobsForAssessment: Job[];
  onAssessForRole: (job: Job) => void;
}

const AssessmentTab: React.FC<AssessmentTabProps> = ({
  jobsLoading,
  jobsForAssessment,
  onAssessForRole,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);

  return (
    <div className="assessment-tab">
      <div className="assessment-header">
        <h3>{t("candidates.detail.aiRoleAssessment")}</h3>
        <p>{t("candidates.detail.aiRoleAssessmentDescription")}</p>
      </div>

      {jobsLoading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t("common:loading.availableJobs")}</p>
        </div>
      ) : jobsForAssessment.length === 0 ? (
        <div className="empty-state">
          <p>{t("candidates.detail.noJobsForAssessment")}</p>
        </div>
      ) : (
        <div className="jobs-list">
          <h4>{t("candidates.detail.availableJobsForAssessment")}</h4>
          <div className="jobs-grid">
            {jobsForAssessment.map((job) => (
              <div key={job.id} className="job-card">
                <div className="job-header">
                  <h5>{job.title}</h5>
                  <span className="job-department">{job.department}</span>
                </div>
                <div className="job-details">
                  <div className="job-info">
                    <span className="job-location">
                      <MapPin size={14} /> {job.location}
                    </span>
                    <span className="job-seniority">
                      <User size={14} /> {job.seniority}
                    </span>
                    <span className="job-type">
                      <Briefcase size={14} /> {job.employment_type}
                    </span>
                  </div>
                  {job.skills && job.skills.length > 0 && (
                    <div className="job-skills">
                      <strong>{t("candidates.detail.requiredSkills")}:</strong>
                      <div className="skills-tags">
                        {job.skills.slice(0, 3).map((skill, index) => (
                          <span key={index} className="skill-tag">
                            {skill}
                          </span>
                        ))}
                        {job.skills.length > 3 && (
                          <span className="skill-tag more">
                            +{job.skills.length - 3} {t("common:misc.more")}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {job.description && (
                    <div className="job-description">
                      <p>{job.description.substring(0, 150)}...</p>
                    </div>
                  )}
                </div>
                <div className="job-actions">
                  <button
                    onClick={() => onAssessForRole(job)}
                    className="btn-primary"
                  >
                    <Bot size={16} /> {t("candidates.detail.assessForRole")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentTab;
